import type React from 'react';
import { useEffect } from 'react';

import { ReactComponent as InfoIcon } from 'icons/ico-info-blue.svg';
import { useTranslations } from 'utils/translations/useTranslations';

import './ChinaOfferSection.scss';
import { setFieldValue } from 'actions/ui/ui';
import {
  OFFER_CHINA_ALIPAY_PAYER_RELATIONSHIP,
  OFFER_CHINA_WECHAT_PAYER_RELATIONSHIP,
  OFFER_STUDENT_IDENTIFICATION_TYPE,
  OFFER_STUDENT_NATIONALITY,
} from 'constants/fields';
import {
  CHINA_CNY_PAYMENT_OPTIONS,
  CHINA_PAYMENT_OPTIONS_WITH_CUSTOM_PAYER_RELATIONSHIP_INFO,
} from 'constants/offers';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers/types';
import { getFieldValue, getFieldValues } from 'selectors';
import { useDispatch } from 'store/configureStore';
import { createMarkup } from 'utils/createMarkup/createMarkup';

const CHINESE = 'zh-CN';

function ChinaOfferSection({
  children,
  paymentOption,
}: {
  children: React.ReactNode;
  paymentOption: string;
}) {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const studentNationality = useSelector((state: RootState) => {
    return getFieldValue(state, OFFER_STUDENT_NATIONALITY);
  });
  useEffect(() => {
    if (!CHINA_CNY_PAYMENT_OPTIONS.includes(paymentOption)) {
      return;
    }
    if (studentNationality === 'mainland_citizen') {
      dispatch(setFieldValue(OFFER_STUDENT_IDENTIFICATION_TYPE, 'NATIONAL_ID'));
    } else if (studentNationality === 'non_mainland_citizen') {
      dispatch(setFieldValue(OFFER_STUDENT_IDENTIFICATION_TYPE, 'PASSPORT'));
    }
  }, [studentNationality, dispatch, paymentOption]);

  const shouldDisplayPayerRelationshipInfo =
    CHINA_PAYMENT_OPTIONS_WITH_CUSTOM_PAYER_RELATIONSHIP_INFO.includes(
      paymentOption,
    );

  if (shouldDisplayPayerRelationshipInfo) {
    return <PayerRelationshipInfo>{children}</PayerRelationshipInfo>;
  }

  return (
    <div className="ChinaOfferSection">
      <div className="ChinaOfferSection--Header">
        <div>
          <InfoIcon aria-hidden={true} />
        </div>
        <div className="ChinaOfferSection--Content">
          <h2>{i18n.t('senderInformation.offer.cn.title')}</h2>
          <p>{i18n.t('senderInformation.offer.cn.description')}</p>{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={
              i18n.currentLocale === CHINESE
                ? 'https://help.flywire.com/hc/zh-cn/sections/4414481606929'
                : 'https://help.flywire.com/hc/en-us/sections/4414481606929'
            }
            data-testid="learn-more"
          >
            {i18n.t('senderInformation.offer.more')}
          </a>
        </div>
      </div>
      {children}
    </div>
  );
}
function PayerRelationshipInfo({
  children,
}: {
  children: React.ReactNode;
}) {
  const i18n = useTranslations();
  const fieldValues = useSelector(getFieldValues);

  // This is a bit hacky, can't we have the same id for both fields or other way to identify them?
  const payerRelationship =
    fieldValues[OFFER_CHINA_ALIPAY_PAYER_RELATIONSHIP] ||
    fieldValues[OFFER_CHINA_WECHAT_PAYER_RELATIONSHIP];

  return payerRelationship ? (
    <div className="ChinaOfferSection">
      <div className="ChinaOfferSection--Header">
        <div>
          <InfoIcon aria-hidden={true} />
        </div>
        <div className="ChinaOfferSection--Content">
          <h2>
            {i18n.t('senderInformation.offer.cn.payerRelationship.title')}
          </h2>
          <p
            dangerouslySetInnerHTML={createMarkup(
              i18n.t(
                'senderInformation.offer.cn.payerRelationship.description',
              ),
            )}
          />
        </div>
      </div>
      {children}
    </div>
  ) : (
    children
  );
}

ChinaOfferSection.displayName = 'ChinaOfferSection';

export { ChinaOfferSection };
